import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Info } from 'lucide-react';
import type {
  ClassificationLevelDetail,
  RecordClassificationEntry,
} from '../../utils/monitoringClassification';
import { formatScoreAsPercentage } from '../../utils/monitoringClassification';

const LEVEL_LABELS = ['Level 1', 'Level 2', 'Level 3'] as const;
const TOOLTIP_MAX_WIDTH = 320;
const VIEWPORT_PAD = 8;

function hasDescription(level: ClassificationLevelDetail): boolean {
  return Boolean(level.description && level.description !== '—');
}

function LevelDescriptionTooltip({
  description,
  children,
}: {
  description: string;
  children: ReactNode;
}) {
  const triggerRef = useRef<HTMLDivElement>(null);
  const tipRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0, width: TOOLTIP_MAX_WIDTH, placeAbove: true });

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const width = Math.min(TOOLTIP_MAX_WIDTH, window.innerWidth - VIEWPORT_PAD * 2);
    let left = rect.left + rect.width / 2 - width / 2;
    left = Math.max(VIEWPORT_PAD, Math.min(left, window.innerWidth - width - VIEWPORT_PAD));

    const tipHeight = tipRef.current?.offsetHeight ?? 0;
    const spaceAbove = rect.top - VIEWPORT_PAD;
    const spaceBelow = window.innerHeight - rect.bottom - VIEWPORT_PAD;
    const placeAbove = tipHeight === 0 ? true : spaceAbove >= tipHeight + 8 || spaceAbove >= spaceBelow;

    const top = placeAbove ? rect.top - 8 : rect.bottom + 8;
    setCoords({ top, left, width, placeAbove });
  }, []);

  useLayoutEffect(() => {
    if (!visible) return;
    updatePosition();
    const frame = requestAnimationFrame(() => updatePosition());
    return () => cancelAnimationFrame(frame);
  }, [visible, description, updatePosition]);

  useEffect(() => {
    if (!visible) return;
    const onReposition = () => updatePosition();
    window.addEventListener('scroll', onReposition, true);
    window.addEventListener('resize', onReposition);
    return () => {
      window.removeEventListener('scroll', onReposition, true);
      window.removeEventListener('resize', onReposition);
    };
  }, [visible, updatePosition]);

  return (
    <>
      <div
        ref={triggerRef}
        className="inline-flex max-w-full"
        onMouseEnter={() => setVisible(true)}
        onMouseLeave={() => setVisible(false)}
        onFocus={() => setVisible(true)}
        onBlur={() => setVisible(false)}
      >
        {children}
      </div>
      {visible &&
        createPortal(
          <div
            ref={tipRef}
            role="tooltip"
            style={{
              position: 'fixed',
              top: coords.top,
              left: coords.left,
              width: coords.width,
              transform: coords.placeAbove ? 'translateY(-100%)' : undefined,
              zIndex: 1000,
            }}
            className="pointer-events-none rounded-lg bg-gray-700 px-3 py-2 text-xs font-medium leading-relaxed text-white break-words shadow-xl"
          >
            {description}
            <div
              className={`absolute left-1/2 -translate-x-1/2 border-4 border-x-transparent ${
                coords.placeAbove
                  ? 'top-full border-t-gray-700 border-b-transparent'
                  : 'bottom-full border-b-gray-700 border-t-transparent'
              }`}
            />
          </div>,
          document.body,
        )}
    </>
  );
}

function LevelSection({
  label,
  level,
}: {
  label: string;
  level: ClassificationLevelDetail;
}) {
  const descriptionAvailable = hasDescription(level);

  const labelText = (
    <>
      {label}
      {level.name !== '—' && (
        <span className="ml-1.5 font-normal text-gray-500">— {level.name}</span>
      )}
    </>
  );

  return (
    <div
      className="rounded-lg border border-gray-100 bg-gray-50/60 px-3 py-2.5"
      onClick={(e) => e.stopPropagation()}
    >
      {descriptionAvailable ? (
        <LevelDescriptionTooltip description={level.description!}>
          <p className="inline-flex max-w-full items-center gap-1.5 text-sm font-medium text-gray-800 cursor-help">
            <span className="min-w-0 break-words">{labelText}</span>
            <Info className="h-3.5 w-3.5 shrink-0 text-emerald-600" aria-hidden="true" />
            <span className="sr-only">Has description</span>
          </p>
        </LevelDescriptionTooltip>
      ) : (
        <p className="text-sm font-medium text-gray-800">{labelText}</p>
      )}
    </div>
  );
}

function ClassificationEntryPanel({ entry }: { entry: RecordClassificationEntry }) {
  return (
    <div
      className={`rounded-lg border px-3 py-3 space-y-2 ${
        entry.isSelected
          ? 'border-emerald-200 bg-emerald-50/30'
          : 'border-gray-200 bg-white'
      }`}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-1 border-b border-gray-100 pb-2">
        <p className="text-sm text-gray-800 shrink-0">
          <span className="font-medium text-gray-500">Ranking score: </span>
          <span className="font-mono font-semibold">{formatScoreAsPercentage(entry.rankingScore)}</span>
        </p>
      </div>
      {entry.isSelected && (
        <span className="inline-block text-xs font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">
          Selected classification
        </span>
      )}
      {entry.note && (
        <p className="text-xs text-gray-600 leading-relaxed">
          <span className="font-medium text-gray-500">Note: </span>
          {entry.note}
        </p>
      )}

      <div className="space-y-1.5 pt-1">
        {entry.levels.map((level, index) => (
          <LevelSection key={LEVEL_LABELS[index]} label={LEVEL_LABELS[index]} level={level} />
        ))}
      </div>
    </div>
  );
}

export default function ClassificationLevelBlock({
  entries,
}: {
  entries: RecordClassificationEntry[];
}) {
  return (
    <div className="space-y-3" onClick={(e) => e.stopPropagation()}>
      {entries.map((entry) => (
        <ClassificationEntryPanel key={entry.classificationId} entry={entry} />
      ))}
    </div>
  );
}
